import type { ScopeNode } from "../types/scope";
import { SCOPE_TYPES } from "../constants/scopeTypes";

/** Builders for the scope-picker trigger label and summary text. */

const pluralize = (n: number, singular: string): string => (n === 1 ? `${n} ${singular}` : `${n} ${singular}s`);

/** Every node id in the selection, grouped by type (used by both the
 * trigger summary and the "shallowest selected type" label below). */
function groupSelectionByType(nodesById: Map<string, ScopeNode>, selectedIds: string[]): Map<ScopeNode["type"], number> {
  const byType = new Map<ScopeNode["type"], number>();
  for (const id of selectedIds) {
    const node = nodesById.get(id);
    if (!node) continue;
    byType.set(node.type, (byType.get(node.type) ?? 0) + 1);
  }
  return byType;
}

export function indexScopeTree(tree: ScopeNode[]): Map<string, ScopeNode> {
  const map = new Map<string, ScopeNode>();
  const walk = (nodes: ScopeNode[]) => {
    for (const node of nodes) {
      map.set(node.id, node);
      if (node.children?.length) walk(node.children);
    }
  };
  walk(tree);
  return map;
}

export function scopeTriggerSummary(tree: ScopeNode[], selectedIds: string[], placeholder: string): string {
  if (selectedIds.length === 0) return placeholder;
  const nodesById = indexScopeTree(tree);
  if (selectedIds.length === 1) {
    return nodesById.get(selectedIds[0])?.name ?? "1 selected";
  }
  const byType = groupSelectionByType(nodesById, selectedIds);
  return Array.from(byType.entries())
    .sort((a, b) => (SCOPE_TYPES[a[0]]?.order ?? 0) - (SCOPE_TYPES[b[0]]?.order ?? 0))
    .map(([type, n]) => pluralize(n, SCOPE_TYPES[type]?.label ?? "Item"))
    .join(" · ");
}

export function scopeTriggerLabel(tree: ScopeNode[], selectedIds: string[], fallback: string): string {
  const nodesById = indexScopeTree(tree);
  const byType = groupSelectionByType(nodesById, selectedIds);
  if (byType.size === 0) return fallback;
  let topType: ScopeNode["type"] | null = null;
  let topOrder = Number.MAX_SAFE_INTEGER;
  for (const type of byType.keys()) {
    const order = SCOPE_TYPES[type]?.order ?? Number.MAX_SAFE_INTEGER;
    if (order < topOrder) {
      topType = type;
      topOrder = order;
    }
  }
  return (topType && SCOPE_TYPES[topType]?.label) || fallback;
}

export function childTypeSummary(node: ScopeNode): { type: ScopeNode["type"]; count: number }[] {
  if (!node.children?.length) return [];
  const counts = new Map<ScopeNode["type"], number>();
  for (const child of node.children) counts.set(child.type, (counts.get(child.type) ?? 0) + 1);
  return Array.from(counts.entries())
    .sort((a, b) => (SCOPE_TYPES[a[0]]?.order ?? 0) - (SCOPE_TYPES[b[0]]?.order ?? 0))
    .map(([type, count]) => ({ type, count }));
}
